import React, { useState } from "react";
import { Routes, Route, NavLink, Navigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, Gift, Crown, LayoutGrid, ShoppingBag, Heart,
  Settings, Sparkles, Mail, Star, HelpCircle, History, LogOut, ExternalLink, KeyRound, Loader2, Building2, FileText,
} from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import ResourceManager from "@/admin/pages/ResourceManager";
import Dashboard from "@/admin/pages/Dashboard";
import Orders from "@/admin/pages/Orders";
import Homepage from "@/admin/pages/Homepage";
import Leads from "@/admin/pages/Leads";
import Personalizations from "@/admin/pages/Personalizations";
import Audit from "@/admin/pages/Audit";
import CorporateEnquiries from "@/admin/pages/CorporateEnquiries";
import CustomSilverEnquiries from "@/admin/pages/CustomSilverEnquiries";
import ContentManager from "@/admin/pages/ContentManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, key: "dashboard", end: true },
  { to: "/admin/products", label: "Products", icon: Package, key: "products" },
  { to: "/admin/parcels", label: "Parcels", icon: Gift, key: "parcels" },
  { to: "/admin/founder", label: "Founder", icon: Crown, key: "founder" },
  { to: "/admin/collections", label: "Collections", icon: LayoutGrid, key: "collections" },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, key: "orders" },
  { to: "/admin/personalizations", label: "Personalizations", icon: Heart, key: "personalizations" },
  { to: "/admin/corporate", label: "Corporate", icon: Building2, key: "corporate" },
  { to: "/admin/custom-silver", label: "Custom Silver", icon: Sparkles, key: "custom-silver" },
  { to: "/admin/homepage", label: "Homepage & Settings", icon: Settings, key: "homepage" },
  { to: "/admin/content", label: "Content Manager", icon: FileText, key: "content" },
  { to: "/admin/vision", label: "Our Vision", icon: Sparkles, key: "vision" },
  { to: "/admin/leads", label: "Notify Leads", icon: Mail, key: "leads" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, key: "reviews" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, key: "faqs" },
  { to: "/admin/audit", label: "Activity Log", icon: History, key: "audit" },
];

function ChangePasswordDialog() {
  const { changePassword } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (pw.length < 8) return toast.error("New password must be at least 8 characters.");
    setLoading(true);
    try {
      await changePassword(pw, cur);
      toast.success("Password updated");
      setOpen(false); setCur(""); setPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-lg" data-testid="admin-change-password-open"><KeyRound className="mr-1.5 h-4 w-4" /> Password</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-xl">Change password</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="mb-1.5 block text-sm">Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-wp-md" /></div>
          <div><Label className="mb-1.5 block text-sm">New password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-wp-md" /></div>
        </div>
        <DialogFooter>
          <Button className="rounded-xl bg-wp-rose-strong text-white" onClick={submit} disabled={loading}>{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Update</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const navClass = ({ isActive }) =>
  `flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-wp-blush-100 text-wp-rose-strong" : "text-wp-ink-muted hover:bg-wp-blush-50 hover:text-wp-ink"}`;

export default function AdminLayout() {
  const { me, logout } = useAdminAuth();
  return (
    <div className="min-h-screen bg-wp-surface">
      {/* Sidebar (desktop) */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r border-border bg-card lg:flex">
        <Link to="/admin" className="flex items-center gap-2.5 border-b border-border px-5 py-4">
          <img src="/logo.jpg" alt="Wonder Parcel" className="h-10 w-10 rounded-full object-cover ring-1 ring-wp-beige-200" />
          <div className="leading-none"><p className="font-display text-base text-wp-rose-strong">Wonder Parcel</p><p className="wp-label mt-1 text-[9px] text-wp-ink-muted">Admin</p></div>
        </Link>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((n) => (
            <NavLink key={n.key} to={n.to} end={n.end} className={navClass} data-testid={`admin-nav-${n.key}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <a href="/" target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-wp-ink-muted hover:bg-wp-blush-50"><ExternalLink className="h-4 w-4" /> View store</a>
          <button onClick={logout} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-wp-rose-strong hover:bg-wp-blush-100" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>

      <div className="lg:pl-60">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border bg-card/90 px-4 py-3 backdrop-blur">
          <p className="truncate text-sm text-wp-ink-muted">Signed in as <span className="font-medium text-wp-ink">{me?.email}</span></p>
          <div className="flex items-center gap-1">
            <ChangePasswordDialog />
            <button onClick={logout} className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-wp-rose-strong hover:bg-wp-blush-100 lg:hidden"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="flex gap-1.5 overflow-x-auto border-b border-border bg-card px-3 py-2 lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.key} to={n.to} end={n.end} className={({ isActive }) => `flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-wp-blush-100 text-wp-rose-strong" : "bg-wp-surface text-wp-ink-muted"}`}>
              <n.icon className="h-3.5 w-3.5" /> {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<ResourceManager resource="products" title="Products" />} />
            <Route path="parcels" element={<ResourceManager resource="parcels" title="Parcels" />} />
            <Route path="founder" element={<ResourceManager resource="products" founder title="Founder Collection" />} />
            <Route path="collections" element={<ResourceManager resource="collections" title="Collections" />} />
            <Route path="orders" element={<Orders />} />
            <Route path="personalizations" element={<Personalizations />} />
            <Route path="corporate" element={<CorporateEnquiries />} />
            <Route path="custom-silver" element={<CustomSilverEnquiries />} />
            <Route path="homepage" element={<Homepage />} />
            <Route path="content" element={<ContentManager />} />
            <Route path="vision" element={<ResourceManager resource="vision" title="Our Vision" />} />
            <Route path="leads" element={<Leads />} />
            <Route path="reviews" element={<ResourceManager resource="reviews" title="Reviews" />} />
            <Route path="faqs" element={<ResourceManager resource="faqs" title="FAQs" />} />
            <Route path="audit" element={<Audit />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
